import { apiUrl } from "./client";

export interface User {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  timezone: string;
  weightUnit: "kg" | "lb" | string;
  emailVerifiedAt: string | null;
  isTestUser: boolean;
  termsAcceptedAt: string | null;
  termsVersion: string | null;
  healthAcknowledgedAt: string | null;
  healthAckVersion: string | null;
  ageConfirmedAt: string | null;
  enableRunningFeatures: boolean;
  easyPaceBaselineSecPerKm: number | null;
  maxHeartRateBpm: number | null;
  restingHeartRateBpm: number | null;
  lactateThresholdHr: number | null;
  ageBorn: number | null;
  easyPaceUpdatedAt: string | null;
  primaryProduct: string;
  createdAt: string;
  updatedAt: string;
  deletionRequestedAt: string | null;
}

export interface ActivitySummary {
  id: string;
  activityType: string;
  activityName: string;
  durationMinutes: number | null;
  distanceMeters: number | null;
  intensityLevel: string;
  activityDate: string;
  activityStartTime: string | null;
  averageHeartrate: number | null;
  maxHeartrate: number | null;
  elevationGainM: number | null;
  perceivedEffort: number | null;
}

export interface GearItem {
  id: string;
  userId: string;
  gearType: string;
  name: string;
  brand: string | null;
  model: string | null;
  purchaseDate: string | null;
  retiredDate: string | null;
  retired: boolean;
  totalDistanceM: number;
  totalActivities: number;
  totalDurationSeconds: number;
  notes: string | null;
  externalStravaId: string | null;
  externalGarminId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityMediaItem {
  mediaId: string;
  mediaType: string;
  durationSeconds: number | null;
  caption: string | null;
  activityId: string;
  activityName: string;
  activityType: string;
  activityDate: string;
}

export interface ActivityDetail extends ActivitySummary {
  userId: string;
  source: string;
  externalId: string;
  movingTimeSeconds: number | null;
  sufferScore: number | null;
  elevationLossM: number | null;
  avgCadence: number | null;
  runCategory: string | null;
  description: string | null;
  isCommute: boolean;
  calories: number | null;
  deviceName: string | null;
  userNotes: string | null;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  gear: GearItem[];
  media: ActivityMediaItem[];
}

export interface ActivitiesResponse {
  activities: ActivitySummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface WellnessEntry {
  id: string;
  userId: string;
  entryDate: string;
  sleepQuality: number | null;
  muscleSoreness: number | null;
  energyLevel: number | null;
  mood: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  readinessScore?: number | null;
}

export interface DeviceWellnessEntry {
  id: string;
  userId: string;
  date: string;
  provider: string;
  sleepDurationMinutes: number | null;
  sleepScore: number | null;
  deepSleepMinutes: number | null;
  remSleepMinutes: number | null;
  lightSleepMinutes: number | null;
  awakeMinutes: number | null;
  sleepStartTime: string | null;
  sleepEndTime: string | null;
  hrvRmssdMs: number | null;
  hrvScore: number | null;
  restingHeartRate: number | null;
  respiratoryRate: number | null;
  stressAvg: number | null;
  bodyBatteryStart: number | null;
  bodyBatteryEnd: number | null;
  spo2Avg: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface Tag {
  id: number;
  slug: string;
  display: string;
  category: string;
  appliesTo: string[];
  sortOrder: number;
}

export interface PhotosResponse {
  items: ActivityMediaItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface MemoryYearGroup {
  year: number;
  yearsAgo: number;
  activities: {
    activityId: string;
    activityName: string;
    activityType: string;
    activityDate: string;
    photos: { mediaId: string; mediaType: string }[];
  }[];
}

export interface TrainingPlanSummary {
  id: string;
  name: string;
  status: string;
  startDate: string | null;
  raceDate: string | null;
  raceName: string | null;
  daysUntilRace: number | null;
}

export interface PlannedWorkout {
  id: string;
  phaseId: string;
  name: string;
  workoutType: string;
  description: string | null;
  targetDurationMinutes: number | null;
  targetDistanceKm: number | null;
  targetElevationM: number | null;
  sortOrder: number;
  isKeyWorkout: boolean;
  weekNumber: number | null;
  priority: string;
  coachTip: string | null;
  scheduledDate: string | null;
  status: string;
  completedAt: string | null;
  externalActivityId: string | null;
  isOptional: boolean;
}

export interface TrainingPhase {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  colour: string | null;
  workouts: PlannedWorkout[];
}

export interface PlanRaceDetail {
  id: string;
  name: string;
  raceDate: string;
  priority: string;
  priorityLabel: string;
  distanceKm: number | null;
  elevationM: number | null;
  terrainType: string | null;
  location: string | null;
  notes: string | null;
  status: string;
  daysUntil: number;
  climbDensityMPerKm: number;
  kmEffort: number;
  resultTimeMinutes: number | null;
  resultNotes: string | null;
}

export interface TrainingPlanDetail extends TrainingPlanSummary {
  currentPhaseId: string | null;
  phases: TrainingPhase[];
  races: PlanRaceDetail[];
  primaryRace: PlanRaceDetail | null;
  nextRace: PlanRaceDetail | null;
}

export function mediaThumbnailUrl(id: string): string {
  return apiUrl(`/api/v1/media/${id}/thumbnail`);
}

export function mediaDisplayUrl(id: string): string {
  return apiUrl(`/api/v1/media/${id}/display`);
}

export function mediaFullUrl(id: string): string {
  return apiUrl(`/api/v1/media/${id}/full`);
}
